import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/layout/ProtectedRoute';
import { GuestRoute } from './components/layout/GuestRoute';
import { AppLayout } from './components/layout/AppLayout';
import { PageLoader } from './components/ui/PageLoader';
import { useAuth } from './hooks/useAuth';

const LandingPage     = lazy(() => import('./pages/Landing'));
const LoginPage       = lazy(() => import('./pages/Auth/Login'));
const RegisterPage    = lazy(() => import('./pages/Auth/Register'));
const OtpVerifyPage   = lazy(() => import('./pages/Auth/OtpVerify'));
const ForgotPasswordPage = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPasswordPage  = lazy(() => import('./pages/Auth/ResetPassword'));
const HomePage        = lazy(() => import('./pages/Home'));
const KingsQueensPage = lazy(() => import('./pages/KingsQueens'));
const ProfileViewPage = lazy(() => import('./pages/ProfileView'));
const MessagingLayout = lazy(() => import('./pages/Messaging/MessagingLayout'));
const ConversationViewPage = lazy(() => import('./pages/Messaging/ConversationView'));
const SubscriptionPage    = lazy(() => import('./pages/Subscription'));
const PaymentSuccessPage  = lazy(() => import('./pages/Payment/PaymentSuccess'));
const PaymentCancelPage   = lazy(() => import('./pages/Payment/PaymentCancel'));
const SettingsPage        = lazy(() => import('./pages/Settings'));
const ProfileSetupPage = lazy(() => import('./pages/ProfileSetup'));
const ProfileEditPage  = lazy(() => import('./pages/ProfileEdit'));
const MyProfilePage   = lazy(() => import('./pages/MyProfile'));
const VerificationPage = lazy(() => import('./pages/Verification'));
const SupportPage     = lazy(() => import('./pages/Support'));
const WhatsAppRedirect = lazy(() => import('./pages/Support/WhatsAppRedirect'));
const AboutPage       = lazy(() => import('./pages/Static/About'));
const SafetyPage      = lazy(() => import('./pages/Static/Safety'));
const ContactPage     = lazy(() => import('./pages/Static/Contact'));
const CareersPage     = lazy(() => import('./pages/Static/Careers'));
const PrivacyPage     = lazy(() => import('./pages/Static/Privacy'));
const TermsPage       = lazy(() => import('./pages/Static/Terms'));
const CookiesPage     = lazy(() => import('./pages/Static/Cookies'));
const GuidelinesPage  = lazy(() => import('./pages/Static/Guidelines'));
const FAQPage         = lazy(() => import('./pages/Static/FAQ'));
const SuccessStoriesPage = lazy(() => import('./pages/Static/SuccessStories'));
const LikesPage       = lazy(() => import('./pages/Likes'));
const EventsPage      = lazy(() => import('./pages/Events'));
const LeaderboardPage = lazy(() => import('./pages/Leaderboard'));
const BoostsPage      = lazy(() => import('./pages/Boosts'));
const RosesPage       = lazy(() => import('./pages/Roses'));
const BlocksPage      = lazy(() => import('./pages/Blocks'));
const BlogListPage    = lazy(() => import('./pages/Blog'));
const BlogPostPage    = lazy(() => import('./pages/Blog/Post'));

function RootRedirect() {
  const { initialized } = useAuth();
  if (!initialized) return <PageLoader />;
  return <LandingPage />;
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public */}
        <Route path="/"               element={<RootRedirect />} />
        <Route path="/login"          element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/register"       element={<GuestRoute><RegisterPage /></GuestRoute>} />
        <Route path="/verify-otp"     element={<GuestRoute><OtpVerifyPage /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
        <Route path="/reset-password"  element={<GuestRoute><ResetPasswordPage /></GuestRoute>} />

        {/* Public profile view — accessible without login */}
        <Route path="/profiles/:id" element={<ProfileViewPage />} />

        {/* Static pages (public) */}
        <Route path="/about-us"      element={<AboutPage />} />
        <Route path="/safety"     element={<SafetyPage />} />
        <Route path="/contact-us"    element={<ContactPage />} />
        <Route path="/careers"    element={<CareersPage />} />
        <Route path="/privacy-policy" element={<PrivacyPage />} />
        <Route path="/terms-of-service"      element={<TermsPage />} />
        <Route path="/cookies"    element={<CookiesPage />} />
        <Route path="/guidelines" element={<GuidelinesPage />} />
        <Route path="/faq"        element={<FAQPage />} />
        <Route path="/success-stories" element={<SuccessStoriesPage />} />
        <Route path="/go/support"  element={<WhatsAppRedirect />} />
        <Route path="/blog"        element={<BlogListPage />} />
        <Route path="/blog/:slug"  element={<BlogPostPage />} />

        {/* Protected */}
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/home"             element={<HomePage />} />
          <Route path="/kings-and-queens" element={<KingsQueensPage />} />
          <Route path="/messages" element={<MessagingLayout />}>
            <Route path=":id" element={<ConversationViewPage />} />
          </Route>
          <Route path="/subscribe"          element={<SubscriptionPage />} />
          <Route path="/subscription"     element={<SubscriptionPage />} />
          <Route path="/payment-success"  element={<PaymentSuccessPage />} />
          <Route path="/payment-cancel"   element={<PaymentCancelPage />} />
          <Route path="/settings"         element={<SettingsPage />} />
          <Route path="/profile/setup" element={<ProfileSetupPage />} />
          <Route path="/profile/setup/step/:step" element={<ProfileSetupPage />} />
          <Route path="/profile/edit" element={<ProfileEditPage />} />
          <Route path="/profile/edit/step/:step" element={<ProfileEditPage />} />
          <Route path="/profile/me"       element={<MyProfilePage />} />
          <Route path="/verify"           element={<VerificationPage />} />
          <Route path="/support"          element={<SupportPage />} />
          <Route path="/likes"            element={<LikesPage />} />
          <Route path="/events"           element={<EventsPage />} />
          {/* <Route path="/leaderboard"      element={<LeaderboardPage />} /> */}
          <Route path="/boosts"           element={<BoostsPage />} />
          <Route path="/roses"            element={<RosesPage />} />
          <Route path="/blocks"           element={<BlocksPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
